import type { UseMutationResult, QueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import {
    buildScenarioViewSeedFromLiveConfig,
    mapPpaViewMetadataToScenarioViewData,
} from 'components/molecules/PricingSimulatorV3/ScenarioLensSection/scenarioLens.helpers';
import type { LiveViewConfig } from 'components/molecules/PricingSimulatorV3/ScenarioLensSection/scenarioLens.types';
import {
    activateScenarioViewSwitch,
    appendCreatedViewToViewListCache,
    prefetchPpaViewList,
    seedCreatedViewMetadataCache,
    type SelectedViewFromList,
} from 'hooks/ppa/ppaViewPrefetch.helpers';
import { getPpaViewMetadataQueryKey } from 'hooks/ppa/useFetchViewMetadata';
import { fetchPpaViewMetadata } from 'hooks/ppa/usePpaViews';
import type {
    PPACreateViewRequest,
    PPACreateViewResponse,
    PPAGetViewMetadataResponse,
    PPAScopeFilter,
    PPAViewDataSource,
    PPAViewListItem,
} from 'types/ppaViews';
import type { ScenarioViewData } from 'components/molecules/PricingSimulatorV3/ScenarioLensSection/scenarioLens.types';
import type { SimulatorAppliedFilters, SimulatorFilterCategory } from 'components/molecules/PricingSimulatorV3/SimulatorFilterPanel/SimulatorFilterPanel';
import { buildSimulatorPersistFilter } from 'components/molecules/PricingSimulatorV3/SimulatorFilterPanel/simulatorFilterPersist.helpers';
import { sendNotification } from 'utils/helperMethods';
import {
    buildCreateViewPayloadFromLiveConfig,
    buildCreateViewPayloadFromMetadata,
    resolvePpaViewApiErrorMessage,
} from 'utils/ppaHelpers';

export type SaveViewFormState = {
    viewName: string;
    includeAppliedFilters: boolean;
    setAsDefault: boolean;
};

export type UseSaveCurrentViewParams = {
    scenarioId?: number;
    ppaScopeId?: number | null;
    userId: string | null;
    workspaceViewId: number | null;
    isScenarioOwner: boolean;
    appliedScope: PPAScopeFilter[];
    appliedFilters?: SimulatorAppliedFilters;
    filterCategories?: SimulatorFilterCategory[];
    savedViews: PPAViewListItem[];
    saveViewForm: SaveViewFormState;
    getLiveViewConfig?: () => LiveViewConfig | null;
    resolveTemplateViewId: () => number | null;
    getViewNameValidationError: (viewName: string, existingNames: string[]) => string | null;
    createViewMutation: UseMutationResult<PPACreateViewResponse, Error, PPACreateViewRequest>;
    queryClient: QueryClient;
    resetSaveViewForm: () => void;
    setDropdownViewMode: (mode: 'list' | 'save') => void;
    setExpandedViewId: (viewId: number | null) => void;
    setHasNoViewSelected: (value: boolean) => void;
    setWorkspaceViewId: (viewId: number | null) => void;
    setActiveDataSource: (dataSource: PPAViewDataSource) => void;
    onSelectedViewChange?: (selection: SelectedViewFromList) => void;
    onViewLoaded?: () => void;
    showCreateViewNotification: (viewName: string, loaded: boolean) => void;
};

export function useSaveCurrentView({
    scenarioId,
    ppaScopeId = null,
    userId,
    workspaceViewId,
    isScenarioOwner,
    appliedScope,
    appliedFilters,
    filterCategories = [],
    savedViews,
    saveViewForm,
    getLiveViewConfig,
    resolveTemplateViewId,
    getViewNameValidationError,
    createViewMutation,
    queryClient,
    resetSaveViewForm,
    setDropdownViewMode,
    setExpandedViewId,
    setHasNoViewSelected,
    setWorkspaceViewId,
    setActiveDataSource,
    onSelectedViewChange,
    onViewLoaded,
    showCreateViewNotification,
}: UseSaveCurrentViewParams) {
    const saveCurrentView = useCallback(async () => {
        const trimmedName = saveViewForm.viewName.trim();
        if (
            !trimmedName ||
            getViewNameValidationError(
                saveViewForm.viewName,
                savedViews.map((view) => view.view_name),
            )
        ) {
            return;
        }

        const templateViewId = resolveTemplateViewId();
        if (templateViewId == null) {
            sendNotification('error', 'No view configuration available to save from.');
            return;
        }

        try {
            const filters = saveViewForm.includeAppliedFilters ? appliedScope : [];
            const persistFilter =
                saveViewForm.includeAppliedFilters && appliedFilters
                    ? buildSimulatorPersistFilter(appliedFilters, filterCategories)
                    : undefined;
            const shouldSetAsDefault = saveViewForm.setAsDefault;
            const liveViewConfig = getLiveViewConfig?.() ?? null;
            let templateMetadata: PPAGetViewMetadataResponse | null = null;
            const createPayload =
                liveViewConfig != null
                    ? buildCreateViewPayloadFromLiveConfig(liveViewConfig, {
                          viewName: trimmedName,
                          filters,
                          isDefault: shouldSetAsDefault,
                          scenarioId,
                          persistFilter,
                      })
                    : buildCreateViewPayloadFromMetadata(
                          (templateMetadata = await fetchPpaViewMetadata(templateViewId)),
                          {
                              viewName: trimmedName,
                              filters,
                              isDefault: shouldSetAsDefault,
                              scenarioId,
                              persistFilter,
                          },
                      );
            const response = await createViewMutation.mutateAsync(createPayload);

            let seedSourceMetadata: ScenarioViewData | undefined;
            if (liveViewConfig != null && scenarioId != null) {
                const baseViewId = workspaceViewId ?? templateViewId;
                const baseMetadata = queryClient.getQueryData<ScenarioViewData>(
                    getPpaViewMetadataQueryKey(baseViewId, scenarioId, 'view', userId),
                );
                if (baseMetadata) {
                    seedSourceMetadata = buildScenarioViewSeedFromLiveConfig(
                        baseMetadata,
                        liveViewConfig,
                        {
                            viewId: response.view_id,
                            viewName: response.view_name,
                            isDefault: shouldSetAsDefault,
                            filters: saveViewForm.includeAppliedFilters ? filters : undefined,
                        },
                    );
                }
            }

            const metadataSeeded =
                scenarioId != null
                    ? seedCreatedViewMetadataCache(queryClient, {
                          scenarioId,
                          userId,
                          createdViewId: response.view_id,
                          createdViewName: response.view_name,
                          isDefault: shouldSetAsDefault,
                          sourceViewId:
                              seedSourceMetadata == null && liveViewConfig == null
                                  ? templateViewId
                                  : undefined,
                          sourceMetadata:
                              seedSourceMetadata ??
                              (templateMetadata != null
                                  ? mapPpaViewMetadataToScenarioViewData(templateMetadata)
                                  : undefined),
                          filters: saveViewForm.includeAppliedFilters ? filters : undefined,
                      })
                    : false;

            appendCreatedViewToViewListCache(
                queryClient,
                scenarioId,
                userId,
                {
                    view_id: response.view_id,
                    view_name: response.view_name,
                    is_default: shouldSetAsDefault,
                    created_by: userId,
                    ...(persistFilter ? { persist_filter: persistFilter } : {}),
                },
                ppaScopeId,
            );

            resetSaveViewForm();
            setDropdownViewMode('list');
            setExpandedViewId(null);
            showCreateViewNotification(response.view_name, true);

            if (scenarioId == null) {
                return;
            }

            void (async () => {
                try {
                    await activateScenarioViewSwitch(queryClient, {
                        scenarioId,
                        viewId: response.view_id,
                        isScenarioOwner,
                        userId,
                        preferCachedMetadata: metadataSeeded,
                        ppaScopeId,
                    });

                    setHasNoViewSelected(false);
                    setActiveDataSource('view');
                    setWorkspaceViewId(response.view_id);
                    onSelectedViewChange?.({ viewId: response.view_id, dataSource: 'view' });
                    onViewLoaded?.();
                    void prefetchPpaViewList(queryClient, scenarioId, userId, ppaScopeId);
                } catch (loadError) {
                    setExpandedViewId(null);
                    sendNotification(
                        'error',
                        resolvePpaViewApiErrorMessage(
                            loadError,
                            'View created but failed to load. Please select it from the list.',
                        ),
                    );
                }
            })();
        } catch (error) {
            sendNotification('error', resolvePpaViewApiErrorMessage(error, 'Failed to save view. Please try again.'));
        }
    }, [
        appliedFilters,
        appliedScope,
        createViewMutation,
        filterCategories,
        getLiveViewConfig,
        getViewNameValidationError,
        isScenarioOwner,
        onSelectedViewChange,
        onViewLoaded,
        queryClient,
        resetSaveViewForm,
        resolveTemplateViewId,
        saveViewForm,
        savedViews,
        scenarioId,
        setActiveDataSource,
        setDropdownViewMode,
        setExpandedViewId,
        setHasNoViewSelected,
        setWorkspaceViewId,
        showCreateViewNotification,
        userId,
        ppaScopeId,
        workspaceViewId,
    ]);

    return { saveCurrentView };
}
